import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import api from '../../../utils/api';
import EmailReplyNotice from './EmailReplyNotice';
import { useAuth } from '../../../context/AuthContext';

const EmailCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [selectedResult, setSelectedResult] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      const res = await api(`/email-automation/campaigns/${id}`);
      if (!res.ok) throw new Error('Failed to fetch campaign details');
      const data = await res.json();
      setCampaign(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (result) => {
    setSelectedResult(result);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedResult(null);
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading campaign details...</div>
      </MainLayout>
    );
  }

  if (!campaign) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>Campaign not found</div>
      </MainLayout>
    );
  }

  const smtpEmail = user?.emailSmtpConfig?.user || '';

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <button 
          onClick={() => navigate('/automation/email/history')} 
          style={{ marginBottom: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: 0 }}
        >
          ← Back to History
        </button>
        
        {/* Campaign Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>{campaign.subject}</h1>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
              Sent on {new Date(campaign.createdAt).toLocaleString()} by {campaign.createdByName}
            </p>
          </div>
          <span style={{ 
            padding: '6px 12px', 
            borderRadius: '6px', 
            fontSize: '14px',
            fontWeight: '500',
            background: campaign.status === 'completed' ? '#dcfce7' : campaign.status === 'failed' ? '#fee2e2' : '#f1f5f9',
            color: campaign.status === 'completed' ? '#166534' : campaign.status === 'failed' ? '#991b1b' : '#475569'
          }}>
            {campaign.status.toUpperCase()}
          </span>
        </div>

        {/* Stats Strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{campaign.stats?.totalSelected}</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Total Selected</div>
          </div>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{campaign.stats?.sent}</div>
            <div style={{ fontSize: '12px', color: '#166534' }}>Sent</div>
          </div>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{campaign.stats?.failed}</div>
            <div style={{ fontSize: '12px', color: '#991b1b' }}>Failed</div>
          </div>
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{campaign.stats?.skipped}</div>
            <div style={{ fontSize: '12px', color: '#b45309' }}>Skipped (No Email)</div>
          </div>
        </div>

        {/* Email Body Preview */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--color-border-primary)', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>📧 Original Email Template</h2>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui, sans-serif', fontSize: '15px', color: 'var(--color-text-primary)', background: 'var(--color-background-secondary)', padding: '16px', borderRadius: '8px' }}>
            {campaign.body}
          </div>
        </div>

        {/* Attachments */}
        {campaign.attachments && campaign.attachments.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--color-text-secondary)' }}>📎 Attachments</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {campaign.attachments.map((att, index) => (
                <div key={index} style={{ padding: '6px 12px', background: 'white', border: '1px solid var(--color-border-primary)', borderRadius: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>📄</span> {att.fileName}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reply Notice */}
        <EmailReplyNotice smtpEmail={smtpEmail} />

        {/* Recipients Table */}
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>👥 Recipients</h2>
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--color-border-primary)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ background: 'var(--color-background-secondary)' }}>
              <tr>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Lead Name</th>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Email</th>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Status</th>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Sent At</th>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Error Details</th>
                <th style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)', textAlign: 'center' }}>View Email</th>
              </tr>
            </thead>
            <tbody>
              {campaign.results && campaign.results.length > 0 ? (
                campaign.results.map((res, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                    <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500' }}>{res.leadName || 'Unknown'}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>{res.email || '—'}</td>
                    <td style={{ padding: '16px', fontSize: '14px' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        background: res.status === 'sent' ? '#dcfce7' : (res.status === 'failed' ? '#fee2e2' : '#fef3c7'),
                        color: res.status === 'sent' ? '#166534' : (res.status === 'failed' ? '#991b1b' : '#b45309')
                      }}>
                        {res.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                      {res.sentAt ? new Date(res.sentAt).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#dc2626' }}>
                      {res.failureReason || '—'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {res.status === 'sent' && res.sentBody ? (
                        <button 
                          onClick={() => openModal(res)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
                          title="View Personalized Email"
                        >
                          👁️
                        </button>
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No results recorded</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Personalized Email Modal */}
        {isModalOpen && selectedResult && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>📧 Email sent to: {selectedResult.leadName}</h3>
                <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--color-text-secondary)' }}>✕</button>
              </div>
              <div style={{ padding: '24px', overflowY: 'auto' }}>
                <div style={{ background: 'var(--color-background-secondary)', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                  <div style={{ marginBottom: '8px' }}><strong style={{ color: 'var(--color-text-secondary)', display: 'inline-block', width: '70px' }}>To:</strong> {selectedResult.email}</div>
                  <div style={{ marginBottom: '8px' }}><strong style={{ color: 'var(--color-text-secondary)', display: 'inline-block', width: '70px' }}>Subject:</strong> {selectedResult.sentSubject}</div>
                  <div><strong style={{ color: 'var(--color-text-secondary)', display: 'inline-block', width: '70px' }}>Sent At:</strong> {new Date(selectedResult.sentAt).toLocaleString()}</div>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui, sans-serif', fontSize: '15px', color: 'var(--color-text-primary)' }}>
                  {selectedResult.sentBody}
                </div>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-primary)', textAlign: 'right' }}>
                <button onClick={closeModal} style={{ padding: '8px 16px', background: 'white', border: '1px solid var(--color-border-primary)', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>Close</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default EmailCampaignDetail;
